import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, ShieldAlert, KeyRound } from "lucide-react";
import { invitationApi } from "../../services/invitationApi";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";

export default function StaffActivatePage() {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("verifying"); // verifying | valid | invalid | success
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    invitationApi
      .validate(token)
      .then((r) => {
        setInvitation(r.data);
        setStatus("valid");
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError(t("staffActivate.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await invitationApi.accept(token, password);
      setStatus("success");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-950 flex items-center justify-center p-6" data-testid="staff-activate-page">
      <div className="absolute top-6 right-6">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <img src="/yokolicious-eagle-salute.png" alt="" className="h-10 w-10 object-contain" />
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">{t("staffActivate.title")}</h1>
        </div>

        {status === "verifying" && (
          <p className="text-sm text-gray-500" data-testid="activate-verifying">{t("staffActivate.verifying")}</p>
        )}

        {status === "invalid" && (
          <div className="text-center py-4" data-testid="activate-invalid">
            <ShieldAlert className="mx-auto text-red-500 mb-3" size={40} />
            <p className="text-sm text-gray-600 mb-6">{t("staffActivate.invalidLink")}</p>
            <Link to="/staff/login" className="text-sm font-semibold text-blue-600 hover:underline" data-testid="activate-invalid-login-link">
              {t("staffActivate.goToLogin")}
            </Link>
          </div>
        )}

        {status === "success" && (
          <div className="text-center py-4" data-testid="activate-success">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={40} />
            <p className="text-sm text-gray-600 mb-6">{t("staffActivate.success")}</p>
            <button
              data-testid="activate-success-login-button"
              onClick={() => navigate("/staff/login")}
              className="px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 active:scale-95 transition-all"
            >
              {t("staffActivate.goToLogin")}
            </button>
          </div>
        )}

        {status === "valid" && invitation && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="activate-form">
            <p className="text-sm text-gray-500">{t("staffActivate.subtitle")}</p>
            <p className="text-xs text-gray-500">{t("staffActivate.instructions")}</p>

            <div className="bg-gray-50 rounded-lg p-4 space-y-1 text-sm">
              <p data-testid="activate-email"><span className="text-gray-500">{t("staffActivate.email")}: </span>{invitation.email}</p>
              <p data-testid="activate-role"><span className="text-gray-500">{t("staffActivate.role")}: </span>{invitation.role}</p>
            </div>

            <div>
              <label htmlFor="activate-password" className="text-sm font-medium text-gray-700 mb-1 block">{t("staffActivate.newPassword")}</label>
              <input
                id="activate-password"
                data-testid="activate-password-input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div>
              <label htmlFor="activate-confirm-password" className="text-sm font-medium text-gray-700 mb-1 block">{t("staffActivate.confirmPassword")}</label>
              <input
                id="activate-confirm-password"
                data-testid="activate-confirm-password-input"
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            {error && <p className="text-sm text-red-500" data-testid="activate-error">{error}</p>}
            <button
              data-testid="activate-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
            >
              <KeyRound size={16} />
              {submitting ? t("common.loading") : t("staffActivate.activate")}
            </button>
            <p className="text-xs text-gray-500 text-center">{t("staffActivate.accessNote")}</p>
          </form>
        )}
      </div>
    </div>
  );
}
